import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, ExternalLink, ArrowRight, Lock, RefreshCw } from "lucide-react";

const DEMAND_COLOR = { Rising: "var(--earn)", High: "var(--text)", Steady: "transparent" };

export default function Trends() {
  const { user, usage, refreshUsage } = useAuth();
  const nav = useNavigate();
  const paid = !!user?.paid;
  const [scope, setScope] = useState(user?.city ? "city" : "general");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!paid);
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(null);

  const trendsAvailable = usage?.applies ? usage.features?.trends?.available : true;
  const trendsCost = usage?.features?.trends?.cost ?? 8;

  const scopes = [
    { key: "city", label: "My city", ok: !!user?.city },
    { key: "region", label: "My region", ok: !!user?.region },
    { key: "country", label: "My country", ok: !!user?.country },
    { key: "general", label: "General", ok: true },
  ];

  const load = (s) => {
    setLoading(true); setErr("");
    api.get(`/trends?scope=${s}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail)))
      .finally(() => { setLoading(false); if (paid) refreshUsage(); });
  };
  // Free users browse freely (no coins). Paid users must spend a daily coin, so load on demand.
  useEffect(() => { if (!paid) load(scope); }, [scope, paid]);

  const start = async (t) => {
    if (!user?.paid) {
      toast.info("Starting a trend as your path is a member feature.");
      nav("/paywall");
      return;
    }
    setStarting(t.title);
    try {
      const { data: session } = await api.post("/sessions", {
        title: t.title, source: "trend", summary: t.why,
        pathData: { thisWeek: t.howToStart, summary: t.why },
      });
      toast.success("Path started");
      nav(`/session/${session.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setStarting(null); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="trends-page">
        <div>
          <div className="label-mono flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Trends</div>
          <h1 className="font-head font-bold text-2xl sm:text-3xl mt-1">Money-making trends</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
            {paid
              ? `Pick an area, then load trends. Each load costs ${trendsCost} coins.`
              : "What's gaining demand right now. Browse freely — starting one as your path is a member feature."}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="trends-scope-toggle">
          {scopes.map((s) => (
            <button key={s.key} onClick={() => { if (s.ok) { setScope(s.key); if (paid) setData(null); } }} disabled={!s.ok}
              data-testid={`trends-scope-${s.key}`}
              className="badge"
              style={{
                background: scope === s.key ? "var(--text)" : "transparent",
                color: scope === s.key ? "#fff" : (s.ok ? "var(--text)" : "var(--text-2)"),
                opacity: s.ok ? 1 : 0.45, cursor: s.ok ? "pointer" : "not-allowed",
              }}
              title={s.ok ? "" : "Add your location in Settings to use this"}>
              {s.label}
            </button>
          ))}
        </div>

        {paid && !data && !loading && (
          <div className="pm-card p-4 space-y-2" data-testid="trends-load-card">
            <button onClick={() => load(scope)} disabled={!trendsAvailable}
              className="pm-btn text-sm flex items-center gap-2" data-testid="trends-load-btn">
              <RefreshCw className="w-4 h-4" /> Show {scopes.find((s) => s.key === scope)?.label} trends · {trendsCost} coins
            </button>
            {!trendsAvailable && (
              <p className="text-xs" style={{ color: "var(--warning)" }} data-testid="trends-insufficient-note">
                Trends cost {trendsCost} coins. Buy 50 more or wait for your monthly refresh.
              </p>
            )}
          </div>
        )}

        {loading && <div className="label-mono" data-testid="trends-loading">Loading the latest trends…</div>}
        {err && !loading && <div className="pm-card p-4 text-sm" data-testid="trends-error">{err}</div>}

        {data && !loading && (
          <>
            <div className="label-mono flex items-center gap-2" data-testid="trends-meta">
              <span>Area: {data.area}</span>
              {data.cached && <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> updated {new Date(data.updatedAt).toLocaleDateString()}</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="trends-list">
              {(data.trends || []).map((t, i) => (
                <div key={i} className="pm-card p-4 flex flex-col" data-testid={`trend-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-head font-bold text-lg leading-tight">{t.title}</h3>
                    <span className="badge whitespace-nowrap" style={{ background: DEMAND_COLOR[t.demand] || "transparent", color: t.demand === "Steady" ? "var(--text-2)" : "#fff" }}>{t.demand}</span>
                  </div>
                  <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{t.why}</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <div><span className="label-mono">Start cost:</span> {t.startCost}</div>
                    <div><span className="label-mono">First move:</span> {t.howToStart}</div>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                    {t.link && (
                      <a href={t.link} target="_blank" rel="noreferrer" className="pm-btn-ghost text-xs flex items-center gap-1.5" data-testid={`trend-link-${i}`}>
                        Learn more <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button onClick={() => start(t)} disabled={starting === t.title}
                      className="pm-btn text-xs flex items-center gap-1.5 ml-auto" data-testid={`trend-start-${i}`}>
                      {user?.paid
                        ? (starting === t.title ? "Starting…" : <>Start as my path <ArrowRight className="w-3.5 h-3.5" /></>)
                        : <><Lock className="w-3.5 h-3.5" /> Unlock to start</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs" style={{ color: "var(--text-2)" }}>
              Educational planning only — not financial advice, and no income guarantees.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
